import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TopBar } from "@/components/observer/TopBar";
import { Transcript } from "@/components/observer/Transcript";
import { Board } from "@/components/observer/Board";
import { AskBox } from "@/components/observer/AskBox";
import { ObserverCard } from "@/components/observer/ObserverCard";
import { SessionSummary } from "@/components/observer/SessionSummary";
import { useSessionPoll } from "@/hooks/useSessionPoll";
import { useRecorder } from "@/hooks/useRecorder";
import { useChunkQueue } from "@/hooks/useChunkQueue";
import { listChunks } from "@/lib/localStore";
import { useWakeLock } from "@/hooks/useWakeLock";
import { useReadAloud } from "@/hooks/useReadAloud";
import { requestMeetingAudio, stopStream, takePendingMeetingAudio } from "@/lib/meetingAudio";
import { OnlineHelp } from "@/components/observer/SourcePicker";
import { api, errMsg } from "@/lib/api";
import { loadMicId, saveMicId } from "@/lib/mics";
import { playChime, unlockAudio } from "@/lib/chime";

function useNarrow() {
  const query = "(max-width: 1023px)";
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

// A meeting that is no longer running: its saved summary if there is one (opened again from Meetings, or the page
// was reloaded after End), otherwise the closing note for a private or unknown one.
function FinishedMeeting({ id, meta }) {
  const [sum, setSum] = useState(undefined);
  useEffect(() => {
    api.get(`/sessions/${id}/summary`).then(({ data }) => setSum(data)).catch(() => setSum(null));
  }, [id]);
  if (sum === undefined) return <div className="h-screen bg-[var(--o-bg)]" data-testid="finished-loading" />;
  if (!sum) return <SessionSummary id={id} stats={null} title="Session no longer in memory" isPrivate={meta ? !!meta.private : undefined} />;
  return <SessionSummary id={id} stats={sum.stats} title={sum.title} participants={sum.participants} isPrivate={false} />;
}

export default function SessionPage() {
  const { id } = useParams();
  const { turns, meta, gone, connected, synced, refresh, setMeta, markGone } = useSessionPoll(id);
  const { enqueue, markSilent, queued, uplinkOk } = useChunkQueue(id, !!meta?.private);
  const [asking, setAsking] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [ending, setEnding] = useState(false); // ending can take ~30 s (last chapter); a second tap must not send another End
  const [ended, setEnded] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const lastFlag = useRef(null);
  const [meeting, setMeeting] = useState(() => takePendingMeetingAudio(id));
  const [micId, setMicId] = useState(loadMicId);
  const [pane, setPane] = useState("transcript");
  // Below the lg breakpoint there isn't room for transcript and answers side by side (stacked, the answers got a
  // sliver at the bottom edge, off-screen on phones): each gets the full height, switched by tabs.
  const narrow = useNarrow();
  const [seenAnswers, setSeenAnswers] = useState(0);
  const chooseMic = (next) => {
    saveMicId(next);
    setMicId(next);
  };
  const online = meta?.audio?.source === "online";

  const meetingRef = useRef(meeting);
  meetingRef.current = meeting;
  useEffect(() => {
    if (!meeting) return undefined;
    const track = meeting.getAudioTracks()[0];
    const onEnded = () => {
      setMeeting(null);
      toast.warning("Meeting audio stopped (tab closed or sharing stopped). Observer now hears only your mic.");
    };
    track?.addEventListener("ended", onEnded);
    return () => track?.removeEventListener("ended", onEnded);
  }, [meeting]);
  // Stop sharing when leaving the page. Deferred so StrictMode's dev-only unmount/remount doesn't kill the share.
  useEffect(() => {
    let mounted = true;
    return () => {
      mounted = false;
      setTimeout(() => !mounted && stopStream(meetingRef.current), 0);
    };
  }, []);

  const connectMeeting = async () => {
    try {
      const stream = await requestMeetingAudio();
      stopStream(meetingRef.current);
      setMeeting(stream);
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    if (!synced || anchor || !meta) return;
    const base = { ms: Date.now() - meta.elapsed * 1000, index: meta.next_chunk_index };
    listChunks(id)
      .then((items) => setAnchor({ ...base, index: Math.max(base.index, ...items.map((c) => c.index + 1)) }))
      .catch(() => setAnchor(base));
  }, [id, synced, meta, anchor]);

  useEffect(() => {
    const fid = meta?.current_flag?.id;
    if (fid && fid !== lastFlag.current && lastFlag.current !== null && meta.settings.chime) playChime();
    if (meta) lastFlag.current = fid || "";
  }, [meta]);

  const liveActive = !!meta && meta.mode === "live" && meta.status === "active" && !meta.paused && !gone && !ended;
  const { capturing, stream, deadMic } = useRecorder({
    active: liveActive, chunkSeconds: meta?.settings.chunk_seconds || 45, baseIndex: anchor?.index, anchorMs: anchor?.ms,
    onChunk: enqueue, onSilent: markSilent, onError: (e) => toast.error(`Microphone unavailable: ${e.message}`),
    onMicMissing: () => {
      toast.warning("The chosen microphone isn't available. Using the system default.");
      chooseMic("");
    },
    micId,
    meeting: online ? meeting : null,
    micTrack: online ? "me" : "room",
  });
  useWakeLock(!!meta && meta.status === "active" && !ended);
  const readAloud = useReadAloud({ sessionId: id, meta, setMeta });

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/sessions/${id}/settings`, body);
      setMeta(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const togglePause = async () => {
    readAloud.release();
    try {
      const { data } = await api.post(`/sessions/${id}/pause`, { paused: !meta.paused });
      setMeta(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doneCount = meta?.answers?.filter((a) => a.status === "done").length || 0;
  useEffect(() => {
    if (pane === "observer") setSeenAnswers(doneCount); // answers read while the panel is open aren't "new" later
  }, [pane, doneCount]);

  const runAsk = async (fn) => {
    unlockAudio();
    if (narrow) setPane("observer");
    setAsking(true);
    setTimeout(refresh, 400);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAsking(false);
      refresh();
    }
  };
  const ask = (q) => runAsk(() => api.post(`/sessions/${id}/ask`, { question: q }, { timeout: 120000 }));
  const pullFlag = (fid) => runAsk(() => api.post(`/sessions/${id}/flags/${fid}/pull`, {}, { timeout: 120000 }));

  const saveFocus = async (focus) => {
    try {
      const { data } = await api.put(`/sessions/${id}/focus`, { focus });
      setMeta(data);
      toast.success("Focus updated. The next answer uses it.");
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };

  const renameSpeaker = useCallback(async (old, name) => {
    try {
      const { data } = await api.post(`/sessions/${id}/speakers/rename`, { old, new: name });
      setMeta(data);
      refresh(); // the poll sees the new speaker_rev and reloads the transcript
      toast.success(`${old} is now ${name}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id, setMeta, refresh]);

  const setBoardItem = async (item, removed) => {
    try {
      const { data } = await api.post(`/sessions/${id}/board/${item.id}`, { removed });
      setMeta(data);
      if (removed) toast("Removed from the board", { action: { label: "Undo", onClick: () => setBoardItem(item, false) } });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const dismiss = async (n, dismissed) => {
    setMeta((m) => ({ ...m, answers: m.answers.map((a) => (a.n === n ? { ...a, dismissed } : a)) }));
    await api.post(`/sessions/${id}/answers/${n}/dismiss`, { dismissed }).catch((e) => toast.error(errMsg(e)));
  };

  const end = async () => {
    if (ending) return;
    setEnding(true);
    readAloud.release();
    await readAloud.stop();
    const closing = toast.loading("Closing the meeting and writing its last chapter…");
    try {
      const { data } = await api.post(`/sessions/${id}/end`);
      markGone();
      stopStream(meetingRef.current);
      setMeeting(null);
      setEnded({ stats: data.stats, title: meta?.title, participants: meta?.participants || [], private: !!data.private });
    } catch (e) {
      toast.error(errMsg(e));
      setEnding(false);
    } finally {
      toast.dismiss(closing);
    }
  };

  if (ended) return <SessionSummary id={id} stats={ended.stats} title={ended.title} participants={ended.participants} isPrivate={ended.private} />;
  if (gone) return <FinishedMeeting id={id} meta={meta} />;
  if (!meta) return <div className="h-[100dvh] bg-[var(--o-bg)]" data-testid="session-loading" />;

  const answers = [...meta.answers].reverse();
  const unseen = narrow && pane !== "observer" ? Math.max(0, doneCount - seenAnswers) : 0;
  const showPane = setPane;
  const tabs = [["transcript", "Transcript"], ["board", `Board${meta.board?.length ? ` · ${meta.board.length}` : ""}`],
    ...(narrow ? [["observer", `Observer${unseen ? ` · ${unseen} new` : ""}`]] : [])];
  const tabBar = (
    <div className="shrink-0 flex items-center gap-1 px-4 sm:px-8 pt-2 border-b border-[var(--o-border-soft)]" role="tablist" data-testid="pane-tabs">
      {tabs.map(([k, l]) => (
        <button key={k} role="tab" aria-selected={pane === k} onClick={() => showPane(k)} className={`relative px-3 py-2 -mb-px text-xs font-semibold border-b-2 transition-colors ${pane === k ? "border-[var(--o-fg)] text-[var(--o-fg)]" : "border-transparent text-[var(--o-muted2)] hover:text-[var(--o-fg)]"}`} data-testid={`pane-tab-${k}`}>
          {l}
          {k === "observer" && meta.current_flag && pane !== "observer" && <span className="absolute top-1.5 right-0.5 h-1.5 w-1.5 rounded-full bg-[#FF3B30]" aria-label="A flag is waiting" />}
        </button>
      ))}
    </div>
  );
  const observerOpen = !narrow || pane === "observer";
  const canSpeak = !!meta.voices?.[meta.settings.tts_provider]?.configured && meta.status === "active";

  return (
    <div className="noise h-[100dvh] w-full flex flex-col bg-[var(--o-bg)] text-[var(--o-fg)] overflow-hidden" data-testid="session-page" onClick={unlockAudio}>
      <TopBar meta={meta} meetingStream={meeting} capturing={capturing} stream={stream} deadMic={deadMic} onSettings={patch} onPause={togglePause} onEnd={() => !ending && setConfirmEnd(true)} ending={ending} online={connected && uplinkOk} queued={queued} micId={micId} onMic={chooseMic} />
      {deadMic && liveActive && (
        <div className="px-6 py-2 text-xs border-b border-[var(--o-border)] bg-[#EF4444]/5 text-[#EF4444]" role="alert" data-testid="dead-mic-banner">
          <span className="font-semibold">No audio is reaching your microphone.</span> It may be muted in Windows or macOS, or another app has taken it. Pick another mic beside the level meter, or check the system sound settings.
        </div>
      )}
      {!connected && (
        <div className="px-6 py-2 text-xs font-mono text-[#EF4444] border-b border-[var(--o-border)] flex items-center gap-2" data-testid="offline-banner">
          Connection lost · showing the last saved view · audio keeps recording locally and uploads when the network returns
        </div>
      )}
      {online && !meeting && meta.status === "active" && (
        <div className="px-6 py-3 text-xs border-b border-[var(--o-border)] bg-[#F59E0B]/5 flex flex-wrap items-start gap-x-6 gap-y-3" data-testid="meeting-audio-banner">
          <div className="space-y-2 max-w-2xl">
            <p className="font-mono text-[#F59E0B]">Meeting audio not connected · Observer only hears your mic, not the other people in the call.</p>
            <OnlineHelp />
          </div>
          <button onClick={connectMeeting} className="h-8 px-3 bg-[var(--o-fg)] text-[var(--o-bg)] font-semibold hover:opacity-90 shrink-0" data-testid="connect-meeting-audio">
            Connect meeting audio
          </button>
        </div>
      )}
      {meta.replay?.status === "interrupted" && (
        <div className="px-6 py-2 text-xs font-mono text-[#F59E0B] border-b border-[var(--o-border)]" data-testid="replay-interrupted">Replay interrupted by a server restart. The transcript so far is kept; upload the recording again to continue.</div>
      )}
      {meta.replay?.status === "error" && (
        <div className="px-6 py-2 text-xs font-mono text-[#EF4444] border-b border-[var(--o-border)]" data-testid="replay-error">Replay failed: {meta.replay.error}</div>
      )}
      {narrow && tabBar}
      <div className="relative z-10 flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        <section className={`w-full lg:w-2/3 h-full lg:border-r border-[var(--o-border)] flex-col ${observerOpen && narrow ? "hidden" : "flex"}`} data-testid="transcript-pane">
          {!narrow && tabBar}
          {/* The transcript stays mounted while the board is shown, so its scroll position and search survive. */}
          <div className={`flex-1 min-h-0 flex-col ${pane === "transcript" ? "flex" : "hidden"}`}>
            <Transcript turns={turns} participants={meta.participants} processing={meta.chunks_processing} mode={meta.mode} chapters={meta.chapters} onRenameSpeaker={meta.status === "active" ? renameSpeaker : undefined} />
          </div>
          {pane === "board" && <Board items={meta.board} through={meta.board_through} onRemove={meta.status === "active" ? (item) => setBoardItem(item, true) : undefined} />}
        </section>
        <aside className={`w-full lg:w-1/3 h-full flex-col bg-[var(--o-bg2)] min-h-0 ${observerOpen ? "flex" : "hidden"}`} data-testid="observer-pane">
          <AskBox meta={meta} onAsk={ask} onPullFlag={pullFlag} asking={asking} onFocus={saveFocus} />
          <div className="thin-scroll flex-1 overflow-y-auto p-5 sm:p-6 space-y-3" data-testid="observer-cards">
            {answers.length === 0 && (
              <p className="text-sm text-[var(--o-muted3)] leading-relaxed" data-testid="observer-empty">
                The Observer stays quiet until asked. A red dot beside Ask means a background check found something worth pulling on.
              </p>
            )}
            <AnimatePresence initial={false}>
              {answers.map((a) => (
                <motion.div key={a.n} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                  <ObserverCard a={a} onDismiss={dismiss} canSpeak={canSpeak} speaking={readAloud.speaking.n === a.n ? readAloud.speaking.phase : null} onSpeak={readAloud.speak} live={meta.mode === "live"} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </aside>
      </div>
      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <AlertDialogContent className="bg-[var(--o-bg2)] border-[var(--o-border)] rounded-none" data-testid="end-session-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">End this session?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--o-muted)]">{meta.private ? "Recording stops and the meeting is wiped from the server. Nothing was saved, so it can't be recovered." : "Recording stops, the log is closed, and the in-memory session is cleared. The JSON log stays on this machine."}{queued > 0 && <span className="block mt-2 text-[#EF4444]" data-testid="end-queued-warning">{queued} audio chunk{queued > 1 ? "s are" : " is"} still waiting to upload and will be lost.</span>}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[var(--o-border)] bg-transparent" data-testid="end-session-cancel">Keep listening</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-[var(--o-fg)] text-[var(--o-bg)] hover:opacity-90" onClick={end} data-testid="end-session-confirm">End session</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
